import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { useReactToChange } from '../../util/hooks';
import { CONSENT_CATEGORIES, hasConsent } from '../../util/cookieConsent';
import IntercomWidget from '../IntercomWidget/IntercomWidget';

/**
 * The support chat on the Help pages.
 *
 * Brevo Conversations replaces Intercom (approved by Yuko, 18 Oct 2026): Brevo
 * is already one of our processors, keeps data in the EU and is free. It is
 * used as soon as REACT_APP_BREVO_CONVERSATIONS_ID is set; until then the
 * Intercom messenger stays, so the Help pages are never left without a chat.
 *
 * Same consent rule as Intercom: nothing loads until the visitor grants the
 * Functional category, and withdrawing it removes the widget from the page.
 */
const BREVO_ID = process.env.REACT_APP_BREVO_CONVERSATIONS_ID;
const BREVO_SCRIPT = 'https://conversations-widget.brevo.com/brevo-conversations.js';

export const isBrevoChat = () => !!BREVO_ID;

const brevo = (...args) => {
  if (typeof window === 'undefined') return;
  window.BrevoConversations =
    window.BrevoConversations ||
    function queue() {
      (window.BrevoConversations.q = window.BrevoConversations.q || []).push(arguments);
    };
  window.BrevoConversations(...args);
};

const loadBrevo = locale => {
  if (typeof document === 'undefined') return;
  if (window.__brevoConversationsLoaded) {
    brevo('restart');
    return;
  }
  window.BrevoConversationsID = BREVO_ID;
  window.BrevoConversationsSetup = { ...(window.BrevoConversationsSetup || {}), locale };
  brevo('setLocale', locale);
  const script = document.createElement('script');
  script.async = true;
  script.src = BREVO_SCRIPT;
  document.head.appendChild(script);
  window.__brevoConversationsLoaded = true;
};

const BrevoChat = props => {
  const { isAuthenticated, currentUser, allowed, intl } = props;
  const locale = (intl?.locale || 'en').startsWith('fr') ? 'fr' : 'en';
  const key = allowed ? `on:${isAuthenticated ? currentUser?.id?.uuid || 'anon' : 'anon'}` : 'off';

  useReactToChange(key, value => {
    if (value === 'off') return undefined;
    loadBrevo(locale);
    if (isAuthenticated && currentUser) {
      // Lets the team reply by email; the widget treats it as a hint only.
      brevo('updateIntegrationData', {
        email: currentUser.attributes.email || null,
        firstName: currentUser.attributes.profile.displayName || null,
      });
    }
    return () => brevo('kill');
  });

  return null;
};

const SupportChatWidget = props => (isBrevoChat() ? <BrevoChat {...props} /> : <IntercomWidget {...props} />);

SupportChatWidget.defaultProps = { isAuthenticated: false, currentUser: null, allowed: false };
SupportChatWidget.propTypes = {
  isAuthenticated: bool,
  currentUser: propTypes.currentUser,
  allowed: bool,
  consent: object,
};

const mapStateToProps = state => ({
  isAuthenticated: state.Auth.isAuthenticated,
  currentUser: state.user.currentUser,
  allowed: hasConsent(state.cookieConsent.consent, CONSENT_CATEGORIES.FUNCTIONAL),
});

export default compose(connect(mapStateToProps), injectIntl)(SupportChatWidget);
